/**
 * /darts — the rules of every game on the page, as one event-sourced engine.
 *
 * A match is its set-up plus the list of what happened (events). The state is
 * ALWAYS the replay of that list: undo is «drop the last event and replay»,
 * a reload resumes from the saved list, and the statistics are counted by the
 * same code that counts the score — they cannot disagree. Nothing here knows
 * about the screen, the bot or whether the board is real or drawn.
 *
 * Events:
 *   {k:'d', s, m, x?, y?, ax?, ay?} one dart of the player whose turn it is:
 *        sector s (1…20, 25 the bull, 0 a miss), multiplier m (0…3; the bull
 *        is m 2, the outer bull m 1); x, y — where it landed and ax, ay —
 *        where it was aimed (normalised board coordinates, the virtual board
 *        only; kept for the throw analysis, ignored by the rules).
 *   {k:'v', pts, n?, dd?} a whole visit typed on the keypad (a real board):
 *        pts points; n darts used when it finished the leg; dd darts thrown at
 *        a double (optional — only for the checkout percentage).
 *   {k:'h', n} hits in a visit (Bob's 27 typed on the keypad).
 *
 * Rules and their sources:
 *   x01 — WDF/PDC rules: subtract each visit; finish exactly on 0, the last dart
 *         a double or the bull (double out), a double/treble/bull (master out)
 *         or anything (single out); going below 0, to 1 under double/master out,
 *         or to 0 without a legal finishing dart is a bust — the visit scores
 *         nothing and the score goes back to where the visit began. Double in:
 *         nothing counts until a double opens the leg.
 *   cricket — 15–20 and the bull; three marks close a number; marks beyond three
 *         score while some opponent has it open (cut-throat: they score for the
 *         opponents who have it open, and the lowest wins); the leg goes to the
 *         first with everything closed and no fewer points (cut-throat: no more).
 *   Большой раунд, Сектор 20, Набор очков — «Правила вида спорта "дартс"»,
 *         Минспорт № 256 (29.03.2022), п. 5.5.6–5.5.8: one visit per sector 1→20
 *         then the bull, only the sector of the round scores; ten visits at the
 *         20, only the 20 scores; ten visits, everything scores.
 *   Shanghai — rounds 1…N, only the round's number scores at full value; a
 *         single, a double and a treble of it in one visit wins at once.
 *   Around the clock — 1→20 then the bull, in order; the first to the bull wins.
 *   Bob's 27 — start on 27; three darts at D1…D20 then the bull; each hit adds
 *         the double, a visit with none subtracts it; below zero ends the game.
 *   Checkouts — a drill: N finishes from 41…170, three visits each (x01 double
 *         out, busts included); the score is how many were closed.
 */

import { points } from './geometry.js';
import { route, oneDartFinish, legalRest, isFinishingDart } from './checkout.js';

export const MODES = ['x01', 'cricket', 'shanghai', 'atc', 'biground', 'sector20', 'countup', 'bobs27', 'checkouts', 'free'];
export const CRICKET = [20, 19, 18, 17, 16, 15, 25];
const IMPOSSIBLE_VISITS = new Set([163, 166, 169, 172, 173, 175, 176, 178, 179]);

/** The defaults of every mode's options. */
export function defaults(mode) {
    switch (mode) {
        case 'x01': return { start: 501, inRule: 'straight', outRule: 'double', legs: 1 };
        case 'cricket': return { legs: 1, cut: false };
        case 'shanghai': return { rounds: 7 };
        case 'atc': return { variant: 'any' };
        case 'sector20': return { rounds: 10 };
        case 'countup': return { rounds: 10 };
        case 'checkouts': return { count: 10, visits: 3, seed: 1 };
        default: return {};
    }
}

/** Modes played in a fixed number of rounds (everyone gets the same visits). */
export function roundsOf(cfg) {
    const o = cfg.opts || {};
    switch (cfg.mode) {
        case 'shanghai': return o.rounds === 20 ? 20 : 7;
        case 'biground': return 21;
        case 'sector20': return 10;
        case 'countup': return 10;
        case 'bobs27': return 21;
        default: return 0;
    }
}

/** Does this mode accept a typed visit total / typed hits on the real-board keypad? */
export function keypadKind(mode) {
    if (mode === 'x01' || mode === 'countup' || mode === 'sector20' || mode === 'biground') return 'total';
    if (mode === 'bobs27') return 'hits';
    return null;
}

export function impossibleVisit(pts) { return pts < 0 || pts > 180 || IMPOSSIBLE_VISITS.has(pts); }

/* ── a tiny deterministic generator for the checkout drill (same seed → same numbers) ── */
function sfc(seed) {
    let a = 0x9e3779b9 ^ seed, b = 0x243f6a88 + seed * 7, c = 0xb7e15162 ^ (seed << 3), d = 1;
    return () => {
        a >>>= 0; b >>>= 0; c >>>= 0; d >>>= 0;
        const t = (a + b + d) >>> 0;
        d = (d + 1) >>> 0;
        a = b ^ (b >>> 9);
        b = (c + (c << 3)) >>> 0;
        c = ((c << 21) | (c >>> 11)) >>> 0;
        c = (c + t) >>> 0;
        return t / 4294967296;
    };
}

/** The numbers of a checkout drill: finishable, spread over 41…170, no repeats. */
export function checkoutNumbers(count, seed) {
    const r = sfc((seed | 0) || 1);
    for (let i = 0; i < 8; i++) r();
    const pool = [];
    for (let v = 41; v <= 170; v++) if (route(v, 3, 'double')) pool.push(v);
    const out = [];
    // spread: a third of them low (41–80), a third mid (81–120), a third high
    const bands = [[41, 80], [81, 120], [121, 170]];
    for (let i = 0; i < count; i++) {
        const [lo, hi] = bands[i % 3];
        const cand = pool.filter((v) => v >= lo && v <= hi && out.indexOf(v) < 0);
        out.push(cand[Math.floor(r() * cand.length)]);
    }
    // the drill climbs: easy first
    return out.sort((x, y) => x - y);
}

function blankStats() {
    return { darts: 0, pts: 0, visits: 0, f9pts: 0, f9darts: 0, t60: 0, t100: 0, t140: 0, t180: 0,
        dblAtt: 0, dblHit: 0, hiCo: 0, bestLeg: 0, legDarts: 0, legVisits: 0, marks: 0, hits: 0, legs: 0, dbls: {} };
}

function freshPlayerLeg(p, cfg) {
    const o = cfg.opts || {};
    p.rest = o.start || 501;
    p.opened = o.inRule !== 'double';
    p.marks = { 20: 0, 19: 0, 18: 0, 17: 0, 16: 0, 15: 0, 25: 0 };
    p.cpts = 0;
    p.target = 1;
    p.st.legDarts = 0;
    p.st.legVisits = 0;
}

/** A new match from its set-up. cfg = {mode, opts, players:[{name, kind, level}]} */
export function createMatch(cfg) {
    const opts = Object.assign(defaults(cfg.mode), cfg.opts || {});
    cfg = Object.assign({}, cfg, { opts });
    const n = Math.max(1, cfg.players.length);
    const s = {
        cfg,
        players: cfg.players.map((pl) => ({ name: pl.name, kind: pl.kind, level: pl.level, legs: 0, round: 0, score: 0,
            out: false, st: blankStats() })),
        cur: 0, legStarter: 0, leg: 1, visit: [], visitStart: null,
        over: false, winner: null, draw: false, log: [], events: 0, last: null,
        // checkout drill
        drill: null,
    };
    s.players.forEach((p) => freshPlayerLeg(p, cfg));
    if (cfg.mode === 'bobs27') s.players.forEach((p) => { p.score = 27; });
    if (cfg.mode === 'checkouts') {
        const nums = checkoutNumbers(opts.count || 10, opts.seed || 1);
        s.drill = { nums, i: 0, visitsLeft: opts.visits || 3, closed: 0, results: [] };
        s.players[0].rest = nums[0];
    }
    s.n = n;
    beginVisit(s);
    return s;
}

function beginVisit(s) {
    const p = s.players[s.cur];
    s.visit = [];
    s.visitStart = { rest: p.rest, opened: p.opened, score: p.score, target: p.target };
}

/** What the current player is throwing at now — for the hint, the bot and the screen. */
export function target(s, pid) {
    pid = pid == null ? s.cur : pid;
    const p = s.players[pid];
    const mode = s.cfg.mode;
    const r = p.round + 1;
    switch (mode) {
        case 'shanghai': return { s: r, m: 0 };
        case 'biground': return r >= 21 ? { s: 25, m: 0 } : { s: r, m: 0 };
        case 'sector20': return { s: 20, m: 0 };
        case 'bobs27': return r >= 21 ? { s: 25, m: 2 } : { s: r, m: 2 };
        case 'atc': return p.target > 20 ? { s: 25, m: s.cfg.opts.variant === 'double' ? 2 : 0 } : { s: p.target, m: s.cfg.opts.variant === 'double' ? 2 : 0 };
        default: return null;
    }
}

/** The checkout route for the current player with the darts left in the visit (x01 and the drill). */
export function hint(s) {
    const mode = s.cfg.mode;
    if (mode !== 'x01' && mode !== 'checkouts') return null;
    const p = s.players[s.cur];
    if (!p.opened) return null;
    const out = mode === 'checkouts' ? 'double' : s.cfg.opts.outRule;
    return route(p.rest, 3 - s.visit.length, out);
}

function outRule(s) { return s.cfg.mode === 'checkouts' ? 'double' : s.cfg.opts.outRule || 'double'; }

/* ── the visit is over: the scores of the visit are booked, the turn moves on ── */
function closeVisit(s, info) {
    const p = s.players[s.cur];
    const mode = s.cfg.mode;
    const darts = info.n != null ? info.n : s.visit.length;
    const total = info.bust ? 0 : (info.total != null ? info.total : s.visit.reduce((a, d) => a + d.pts, 0));
    const st = p.st;
    st.visits++;
    st.legVisits++;
    if (mode === 'x01' || mode === 'checkouts' || mode === 'countup' || mode === 'free') {
        st.darts += darts;
        st.pts += total;
        if (st.legVisits <= 3 && mode === 'x01') { st.f9pts += total; st.f9darts += darts; }
        if (total >= 180) st.t180++;
        else if (total >= 140) st.t140++;
        else if (total >= 100) st.t100++;
        else if (total >= 60) st.t60++;
    } else {
        st.darts += darts;
        st.pts += total;
    }
    st.legDarts += darts;
    s.log.push({ pid: s.cur, leg: s.leg, darts: s.visit.map((d) => ({ s: d.s, m: d.m })), total, bust: !!info.bust,
        rest: (mode === 'x01' || mode === 'checkouts') ? p.rest : null, score: p.score, note: info.note || null, n: darts });
    s.last = { pid: s.cur, total, bust: !!info.bust, note: info.note || null, darts };
}

function nextPlayer(s) {
    const n = s.players.length;
    for (let k = 1; k <= n; k++) {
        const j = (s.cur + k) % n;
        if (!s.players[j].out) { s.cur = j; return true; }
    }
    return false;
}

function finishMatch(s, winner, draw) {
    s.over = true;
    s.winner = winner;
    s.draw = !!draw;
}

/* ── round modes: after every player's visit, check whether the game is over ── */
function afterRoundVisit(s) {
    const p = s.players[s.cur];
    p.round++;
    const R = roundsOf(s.cfg);
    const live = s.players.filter((q) => !q.out);
    const done = live.every((q) => q.round >= R);
    if (done || live.length === 0) {
        // highest score wins (Bob's 27: those who survived beat those knocked out)
        let best = -Infinity, who = [];
        s.players.forEach((q, i) => {
            const v = (q.out ? -1e6 + q.round * 1000 : 0) + q.score;
            if (v > best) { best = v; who = [i]; } else if (v === best) who.push(i);
        });
        finishMatch(s, who.length === 1 ? who[0] : null, who.length > 1);
        return;
    }
    nextPlayer(s);
    beginVisit(s);
}

function legWon(s) {
    const p = s.players[s.cur];
    p.legs++;
    p.st.legs++;
    if (!p.st.bestLeg || p.st.legDarts < p.st.bestLeg) p.st.bestLeg = p.st.legDarts;
    const need = s.cfg.opts.legs || 1;
    if (p.legs >= need) { finishMatch(s, s.cur, false); return true; }
    // a new leg: everyone starts again, the next player in turn opens it
    s.leg++;
    s.players.forEach((q) => freshPlayerLeg(q, s.cfg));
    s.legStarter = (s.legStarter + 1) % s.players.length;
    s.cur = s.legStarter;
    beginVisit(s);
    return false;
}

/* ── one dart ── */
function dartX01(s, d, res) {
    const p = s.players[s.cur];
    const out = outRule(s);
    // a dart at a one-dart finish is a dart «at the double» (the checkout percentage)
    if (p.opened && oneDartFinish(p.rest, out)) {
        p.st.dblAtt++;
        res.atDouble = true;
        // which double: the rest itself tells it (32 → D16, 50 → the bull) — per-double rates
        if (out === 'double') {
            const key = p.rest === 50 ? 25 : p.rest / 2;
            const b = p.st.dbls[key] || (p.st.dbls[key] = [0, 0]);
            b[0]++;
            res.dblKey = key;
        }
    }
    let pts = d.pts;
    if (!p.opened) {
        if (d.m === 2) p.opened = true; // the bull opens too
        else pts = 0;
    }
    d.pts = pts;
    const rest = p.rest - pts;
    if (rest === 0 && isFinishingDart(d.s, d.m, out)) {
        p.rest = 0;
        if (res.atDouble) {
            p.st.dblHit++;
            if (res.dblKey && p.st.dbls[res.dblKey]) p.st.dbls[res.dblKey][1]++;
        }
        p.st.hiCo = Math.max(p.st.hiCo, s.visitStart.rest);
        res.checkout = s.visitStart.rest;
        return 'leg';
    }
    if (!legalRest(rest, out) || rest === 0) {
        p.rest = s.visitStart.rest;
        p.opened = s.visitStart.opened;
        res.bust = true;
        return 'bust';
    }
    p.rest = rest;
    return null;
}

function cricketValue(n) { return n === 25 ? 25 : n; }

function dartCricket(s, d, res) {
    const p = s.players[s.cur];
    if (CRICKET.indexOf(d.s) < 0 || !d.m) { d.pts = 0; return null; }
    const marks = d.s === 25 ? d.m : d.m; // outer bull 1, bull 2
    const cur = p.marks[d.s];
    const toClose = Math.min(marks, Math.max(0, 3 - cur));
    p.marks[d.s] = cur + toClose;
    const extra = marks - toClose;
    let counted = toClose, scored = 0;
    if (extra > 0) {
        const opps = s.players.filter((q, i) => i !== s.cur);
        const openOpps = opps.filter((q) => q.marks[d.s] < 3);
        if (s.cfg.opts.cut) {
            openOpps.forEach((q) => { q.cpts += extra * cricketValue(d.s); });
            if (openOpps.length) { counted += extra; scored = extra * cricketValue(d.s); }
        } else if (openOpps.length) {
            p.cpts += extra * cricketValue(d.s); scored = extra * cricketValue(d.s); counted += extra;
        }
    }
    d.pts = scored;
    p.st.marks += counted;
    res.marks = counted;
    // the leg: everything closed, and the points allow it
    if (CRICKET.every((n) => p.marks[n] >= 3)) {
        const others = s.players.filter((q, i) => i !== s.cur);
        if (!others.length) return 'leg';
        if (s.cfg.opts.cut) {
            if (others.every((q) => p.cpts <= q.cpts)) return 'leg';
        } else if (others.every((q) => p.cpts >= q.cpts)) return 'leg';
    }
    return null;
}

function dartRound(s, d) {
    const p = s.players[s.cur];
    const t = target(s);
    let pts = 0;
    switch (s.cfg.mode) {
        case 'countup': pts = d.pts; break;
        case 'bobs27': pts = 0; break; // booked at the visit's end
        default:
            if (d.s === t.s) pts = d.pts;
    }
    d.pts = pts;
    if (s.cfg.mode !== 'bobs27') { p.score += pts; if (pts) p.st.hits++; }
    else if (d.s === t.s && d.m === 2) { p.st.hits++; d.hit = true; }
    return null;
}

function dartAtc(s, d, res) {
    const p = s.players[s.cur];
    const t = target(s);
    const dbl = s.cfg.opts.variant === 'double';
    const hit = d.s === t.s && (!dbl || d.m === 2);
    d.pts = 0;
    if (hit) {
        p.st.hits++;
        res.advanced = true;
        if (p.target > 20) return 'leg';
        p.target = p.target === 20 ? 21 : p.target + 1;
    }
    return null;
}

/** Applies one event to the state. Returns what happened (for sounds and the screen). */
export function apply(s, ev) {
    const res = { k: ev.k, pid: s.cur };
    if (s.over) return res;
    s.events++;
    const mode = s.cfg.mode;
    const p = s.players[s.cur];

    if (ev.k === 'd') {
        const d = { s: ev.s | 0, m: ev.m | 0, pts: points(ev.s | 0, ev.m | 0), x: ev.x, y: ev.y, ax: ev.ax, ay: ev.ay };
        if (!d.s || !d.m) { d.s = 0; d.m = 0; d.pts = 0; }
        let outcome = null;
        if (mode === 'x01' || mode === 'checkouts') outcome = dartX01(s, d, res);
        else if (mode === 'cricket') outcome = dartCricket(s, d, res);
        else if (mode === 'atc') outcome = dartAtc(s, d, res);
        else if (mode === 'free') { /* free throwing: every dart scores its value */ }
        else outcome = dartRound(s, d);
        s.visit.push(d);
        res.dart = d;
        if (mode === 'shanghai') {
            const t = target(s);
            const ms = new Set(s.visit.filter((x) => x.s === t.s).map((x) => x.m));
            if (ms.has(1) && ms.has(2) && ms.has(3)) outcome = 'shanghai';
        }
        if (outcome === 'bust') {
            closeVisit(s, { bust: true });
            res.visitDone = true;
            afterX01Visit(s, res);
            return res;
        }
        if (outcome === 'leg') {
            res.legWon = true;
            closeVisit(s, { note: 'leg' });
            res.visitDone = true;
            if (mode === 'checkouts') { drillNext(s, true); res.matchOver = s.over; return res; }
            if (mode === 'atc') { p.st.legs++; finishMatch(s, s.cur, false); res.matchOver = true; return res; }
            res.matchOver = legWon(s);
            return res;
        }
        if (outcome === 'shanghai') {
            p.round++;
            closeVisit(s, { note: 'shanghai' });
            res.visitDone = true; res.shanghai = true; res.matchOver = true;
            finishMatch(s, s.cur, false);
            return res;
        }
        if (s.visit.length >= 3) {
            if (mode === 'bobs27') bobsBook(s, s.visit.filter((x) => x.hit).length);
            closeVisit(s, {});
            res.visitDone = true;
            res.total = s.last.total;
            afterVisit(s, res);
        }
        return res;
    }

    if (ev.k === 'v') {
        // a typed visit — real board, keypad
        const pts = Math.max(0, Math.min(180, ev.pts | 0));
        if (mode === 'x01') {
            const out = outRule(s);
            const start = p.rest;
            const dd = Math.max(0, Math.min(3, ev.dd | 0));
            p.st.dblAtt += dd;
            if (!p.opened && pts > 0) p.opened = true; // a typed visit carries its own opening double
            const rest = start - pts;
            if (rest === 0 && checkoutPossible(start, ev.n || 3, out)) {
                p.rest = 0;
                p.st.dblHit++;
                if (!dd) p.st.dblAtt++;
                p.st.hiCo = Math.max(p.st.hiCo, start);
                s.visit = [];
                closeVisit(s, { total: pts, n: Math.max(1, Math.min(3, ev.n || 3)), note: 'leg' });
                res.legWon = true; res.visitDone = true; res.checkout = start;
                res.matchOver = legWon(s);
                return res;
            }
            if (!legalRest(rest, out) || rest === 0) {
                p.rest = s.visitStart.rest; p.opened = s.visitStart.opened;
                closeVisit(s, { bust: true, n: 3 });
                res.bust = true; res.visitDone = true;
                afterX01Visit(s, res);
                return res;
            }
            p.rest = rest;
            closeVisit(s, { total: pts, n: 3 });
            res.visitDone = true; res.total = pts;
            afterX01Visit(s, res);
            return res;
        }
        // round modes with a total: the sum the player read off the board
        p.score += pts;
        if (pts) p.st.hits++;
        closeVisit(s, { total: pts, n: 3 });
        res.visitDone = true; res.total = pts;
        afterVisit(s, res);
        return res;
    }

    if (ev.k === 'h' && mode === 'bobs27') {
        const hits = Math.max(0, Math.min(3, ev.n | 0));
        p.st.hits += hits;
        bobsBook(s, hits);
        closeVisit(s, { total: 0, n: 3, note: 'h' + hits });
        res.visitDone = true;
        afterVisit(s, res);
        return res;
    }
    return res;
}

function checkoutPossible(rest, n, out) { return route(rest, Math.max(1, Math.min(3, n || 3)), out) !== null; }

function bobsBook(s, hits) {
    const p = s.players[s.cur];
    const t = target(s);
    const val = t.s === 25 ? 50 : t.s * 2;
    if (hits > 0) p.score += hits * val;
    else p.score -= val;
    if (p.score < 0) p.out = true;
}

function afterX01Visit(s, res) { afterVisit(s, res); }

function afterVisit(s, res) {
    const mode = s.cfg.mode;
    if (mode === 'checkouts') {
        const d = s.drill;
        d.visitsLeft--;
        if (d.visitsLeft <= 0) { drillNext(s, false); res.matchOver = s.over; return; }
        beginVisit(s);
        return;
    }
    if (roundsOf(s.cfg)) { afterRoundVisit(s); res.matchOver = s.over; return; }
    if (mode === 'free') { beginVisit(s); return; }
    nextPlayer(s);
    beginVisit(s);
}

function drillNext(s, closed) {
    const d = s.drill;
    const p = s.players[0];
    d.results.push({ n: d.nums[d.i], ok: !!closed });
    if (closed) d.closed++;
    d.i++;
    if (d.i >= d.nums.length) { finishMatch(s, 0, false); p.score = d.closed; return; }
    d.visitsLeft = s.cfg.opts.visits || 3;
    p.rest = d.nums[d.i];
    p.score = d.closed;
    beginVisit(s);
}

/** The whole match from its set-up and events (undo = replay without the last one). */
export function replay(cfg, events) {
    const s = createMatch(cfg);
    for (const ev of events) apply(s, ev);
    return s;
}

/** 3-dart average of a player (points per dart × 3). */
export function avg3(st) { return st.darts ? (st.pts / st.darts) * 3 : 0; }
export function first9(st) { return st.f9darts ? (st.f9pts / st.f9darts) * 3 : 0; }
export function checkoutPct(st) { return st.dblAtt ? (st.dblHit / st.dblAtt) * 100 : null; }
export function mpr(st) { return st.visits ? st.marks / st.visits : 0; }

/** Is it a mode where several players make sense? */
export function soloOnly(mode) { return mode === 'checkouts' || mode === 'free'; }
